/**
 * consent.js — the site's consent management layer.
 *
 * Google Analytics is never loaded until the visitor accepts analytics
 * cookies. The decision lives in localStorage for 12 months; "Essential only"
 * or a later revoke disables GA (`ga-disable-*`), pushes a Consent Mode
 * `denied` update and clears the _ga cookies.
 */
import { trackPageview } from "./analytics";

const KEY = "mra_cookie_consent";
const VERSION = 1;
const MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;
const GA_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || "G-0CN4PMVD9E";
const API = process.env.REACT_APP_BACKEND_URL;

const listeners = new Set();
let gtagLoaded = false;

const newCid = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID().replace(/-/g, "");
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
};

/** Random per-browser id (not tied to any account) so the admin opt-in stats count visitors, not clicks. */
const readCid = () => {
  try {
    const raw = window.localStorage.getItem(KEY);
    const cid = raw ? JSON.parse(raw)?.cid : null;
    return typeof cid === "string" && cid.length >= 8 ? cid : newCid();
  } catch {
    return newCid();
  }
};

const report = (consent, source) => {
  if (!API || typeof fetch === "undefined") return;
  fetch(`${API}/api/consent`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ cid: consent.cid, analytics: consent.analytics, source }),
    keepalive: true,
  }).catch(() => {});
};

const read = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (c?.v !== VERSION || !c.at) return null;
    if (Date.now() - new Date(c.at).getTime() > MAX_AGE_MS) return null;
    return c;
  } catch {
    return null;
  }
};

export const getConsent = read;
export const hasDecision = () => read() !== null;

export const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
const emit = (evt) => listeners.forEach((fn) => fn(evt));

/** Open the preferences dialog ("consent center") from anywhere. */
export const openPreferences = () => emit({ type: "open" });

export const setConsent = ({ analytics, source = "banner" }) => {
  const consent = { v: VERSION, analytics: Boolean(analytics), at: new Date().toISOString(), cid: readCid() };
  try {
    window.localStorage.setItem(KEY, JSON.stringify(consent));
  } catch {
    /* storage blocked — decision still applies for this page load */
  }
  if (consent.analytics) enableAnalytics();
  else disableAnalytics();
  report(consent, source);
  emit({ type: "change", consent });
  return consent;
};

/** Apply a stored decision on boot (called once by <CookieConsent />). */
export const initConsent = () => {
  if (read()?.analytics) enableAnalytics();
};

const enableAnalytics = () => {
  if (typeof window === "undefined") return;
  window[`ga-disable-${GA_ID}`] = false;
  if (gtagLoaded) {
    window.gtag("consent", "update", { analytics_storage: "granted" });
    return;
  }
  gtagLoaded = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "granted",
  });
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, { send_page_view: false, anonymize_ip: true });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  s.setAttribute("data-consent", "analytics");
  document.head.appendChild(s);
  trackPageview(`${window.location.pathname}${window.location.search || ""}`);
};

const disableAnalytics = () => {
  if (typeof window === "undefined") return;
  window[`ga-disable-${GA_ID}`] = true;
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", { analytics_storage: "denied" });
  }
  clearGaCookies();
};

const clearGaCookies = () => {
  const names = document.cookie
    .split("; ")
    .map((c) => c.split("=")[0])
    .filter((n) => n === "_ga" || n === "_gid" || n.startsWith("_ga_"));
  const parts = window.location.hostname.split(".");
  const domains = [""];
  for (let i = 0; i < parts.length - 1; i += 1) domains.push(parts.slice(i).join("."));
  names.forEach((n) => domains.forEach((d) => {
    document.cookie = `${n}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=.${d}` : ""}`;
  }));
};
